import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { createPortal } from "react-dom";
import {
	motion,
	AnimatePresence,
	useMotionValue,
	useTransform,
	PanInfo,
} from "framer-motion";

import { ALL_REACTION_TYPES } from "components/ReactionPicker";
import { GetImg } from "utils";
import { useProfileGate } from "utils/useProfileGate";
import { api } from "store";
import styles from "./styles.module.scss";

interface IReactor {
	id: number;
	full_name: string;
	first_name: string;
	picture?: string;
	is_community?: boolean;
	member_directory_opt_in?: boolean;
	profile_visible?: boolean;
	reaction_type: string;
	reacted_at: string;
}

interface IReactorModal {
	open: boolean;
	onClose: () => void;
	reactableType: string;
	reactableId: number;
	reactionsSummary: { type: string; count: number }[];
	totalCount: number;
	currentUserId?: number;
	initialFilter?: string;
}

const getInitials = (name: string) => {
	if (!name) return "?";
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) {
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	}
	return name.slice(0, 2).toUpperCase();
};

const useIsMobile = () => {
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
	useEffect(() => {
		const handler = () => setIsMobile(window.innerWidth <= 600);
		window.addEventListener("resize", handler);
		return () => window.removeEventListener("resize", handler);
	}, []);
	return isMobile;
};

// ─── Shared content ───

const ReactorContent: React.FC<{
	activeTab: string;
	setActiveTab: (tab: string) => void;
	sortedReactions: { type: string; count: number }[];
	totalCount: number;
	loading: boolean;
	sortedReactors: IReactor[];
	currentUserId?: number;
	emptyReaction: { type: string; label: string; emoji: string } | null;
	onNavigate?: (
		id: number,
		memberDirectoryOptIn: boolean | undefined,
		profileVisible: boolean | undefined,
	) => void;
}> = ({
	activeTab,
	setActiveTab,
	sortedReactions,
	totalCount,
	loading,
	sortedReactors,
	currentUserId,
	emptyReaction,
	onNavigate,
}) => (
	<>
		<div className={styles.tabBar}>
			<button
				className={`${styles.tab} ${activeTab === "all" ? styles.activeTab : ""}`}
				onClick={() => setActiveTab("all")}
			>
				All {totalCount}
			</button>
			{sortedReactions.map((r) => {
				const reaction = ALL_REACTION_TYPES.find(
					(rx) => rx.type === r.type,
				);
				if (!reaction) return null;
				return (
					<button
						key={r.type}
						className={`${styles.tab} ${activeTab === r.type ? styles.activeTab : ""}`}
						onClick={() => setActiveTab(r.type)}
					>
						{reaction.emoji} {r.count}
					</button>
				);
			})}
		</div>
		<div className={styles.reactorList}>
			{loading ? (
				<div className={styles.loadingState}>
					<div className={styles.spinner} />
				</div>
			) : sortedReactors.length === 0 ? (
				<div className={styles.emptyState}>
					{emptyReaction && (
						<span className={styles.emptyEmoji}>
							{emptyReaction.emoji}
						</span>
					)}
					<p className={styles.emptyTitle}>
						No {emptyReaction?.label ?? "reactions"} yet
					</p>
					<p className={styles.emptySub}>Be the first to react!</p>
				</div>
			) : (
				sortedReactors.map((reactor, i) => {
					const reaction = ALL_REACTION_TYPES.find(
						(rx) => rx.type === reactor.reaction_type,
					);
					// eslint-disable-next-line eqeqeq
					const isYou =
						currentUserId != null && reactor.id == currentUserId;
					const displayName = isYou
						? "You"
						: reactor.first_name ||
							reactor.full_name?.split(" ")[0] ||
							"Member";
					return (
						<motion.div
							key={`${reactor.id}-${reactor.reaction_type}`}
							className={styles.reactorRow}
							initial={{ opacity: 0, y: 8 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{
								delay: i * 0.03,
								duration: 0.2,
								ease: "easeOut",
							}}
						>
							<div className={styles.avatarWrap}>
								<div className={styles.avatar}>
									{reactor.picture ? (
										<GetImg endpoint={reactor.picture} />
									) : (
										getInitials(reactor.full_name)
									)}
								</div>
								{reaction && (
									<span
										className={styles.avatarReactionBadge}
										aria-label={reaction.label}
									>
										{reaction.emoji}
									</span>
								)}
							</div>
							<div className={styles.reactorInfo}>
								{!isYou ? (
									<span
										className={styles.reactorName}
										onClick={() =>
											onNavigate?.(
												reactor.id,
												reactor.member_directory_opt_in,
												reactor.profile_visible,
											)
										}
										role="button"
										tabIndex={0}
									>
										{displayName}
									</span>
								) : (
									<span className={styles.reactorNameStatic}>
										{displayName}
									</span>
								)}
							</div>
						</motion.div>
					);
				})
			)}
		</div>
	</>
);

// ─── Mobile bottom sheet with framer-motion ───

const MobileSheet: React.FC<{
	open: boolean;
	onClose: () => void;
	children: React.ReactNode;
}> = ({ open, onClose, children }) => {
	const y = useMotionValue(0);
	const backdropOpacity = useTransform(y, [0, 400], [1, 0]);

	const handleDragEnd = useCallback(
		(_: any, info: PanInfo) => {
			// Close if dragged > 100px down OR velocity > 500px/s
			if (info.offset.y > 100 || info.velocity.y > 500) {
				onClose();
			}
		},
		[onClose],
	);

	return createPortal(
		<AnimatePresence>
			{open && (
				<div className={styles.sheetOverlay}>
					{/* Backdrop */}
					<motion.div
						className={styles.sheetBackdrop}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.25 }}
						style={{ opacity: backdropOpacity }}
						onClick={onClose}
					/>

					{/* Sheet */}
					<motion.div
						className={styles.sheet}
						initial={{ y: "100%" }}
						animate={{ y: 0 }}
						exit={{ y: "100%" }}
						transition={{
							type: "spring",
							damping: 30,
							stiffness: 300,
							mass: 0.8,
						}}
						style={{ y }}
						drag="y"
						dragConstraints={{ top: 0 }}
						dragElastic={{ top: 0, bottom: 0.4 }}
						onDragEnd={handleDragEnd}
					>
						{/* Drag handle */}
						<div className={styles.dragZone}>
							<motion.div
								className={styles.dragPill}
								whileTap={{ scaleX: 1.3 }}
							/>
						</div>
						{children}
					</motion.div>
				</div>
			)}
		</AnimatePresence>,
		document.body,
	);
};

// ─── Main component ───

const ReactorModal: React.FunctionComponent<IReactorModal> = ({
	open,
	onClose,
	reactableType,
	reactableId,
	reactionsSummary,
	totalCount,
	currentUserId,
	initialFilter,
}) => {
	const isMobile = useIsMobile();
	const profileGate = useProfileGate({ onBeforeNavigate: onClose });
	const [activeTab, setActiveTab] = useState<string>(initialFilter ?? "all");
	const [reactors, setReactors] = useState<IReactor[]>([]);
	const [loading, setLoading] = useState(false);

	const sortedReactions = useMemo(
		() =>
			reactionsSummary
				.filter((r) => r.count > 0)
				.sort((a, b) => b.count - a.count),
		[reactionsSummary],
	);

	useEffect(() => {
		if (open) setActiveTab(initialFilter ?? "all");
	}, [open, initialFilter]);

	const fetchReactors = useCallback(
		async (reactionType?: string) => {
			setLoading(true);
			try {
				const response = await api.reactions.getReactors({
					reactable_type: reactableType,
					reactable_id: reactableId,
					reaction_type: reactionType,
					limit: 100,
				});
				const resData = response?.data?.data ?? response?.data;
				setReactors(resData?.users ?? []);
			} catch {
				setReactors([]);
			}
			setLoading(false);
		},
		[reactableType, reactableId],
	);

	useEffect(() => {
		if (!open) return;
		fetchReactors(activeTab === "all" ? undefined : activeTab);
	}, [open, activeTab, fetchReactors]);

	const sortedReactors = useMemo(() => {
		if (!currentUserId) return reactors;
		return [...reactors].sort((a, b) => {
			// eslint-disable-next-line eqeqeq
			if (a.id == currentUserId) return -1;
			// eslint-disable-next-line eqeqeq
			if (b.id == currentUserId) return 1;
			return 0;
		});
	}, [reactors, currentUserId]);

	const emptyReaction = useMemo(() => {
		if (activeTab === "all") return null;
		return ALL_REACTION_TYPES.find((r) => r.type === activeTab) ?? null;
	}, [activeTab]);

	const handleNavigate = useCallback(
		(
			id: number,
			memberDirectoryOptIn: boolean | undefined,
			profileVisible: boolean | undefined,
		) => {
			profileGate.open({
				id,
				memberDirectoryOptIn,
				profileVisible,
			});
		},
		[profileGate],
	);

	const content = (
		<ReactorContent
			activeTab={activeTab}
			setActiveTab={setActiveTab}
			sortedReactions={sortedReactions}
			totalCount={totalCount}
			loading={loading}
			sortedReactors={sortedReactors}
			currentUserId={currentUserId}
			emptyReaction={emptyReaction}
			onNavigate={handleNavigate}
		/>
	);

	if (!open) return null;

	if (isMobile) {
		return (
			<>
				<MobileSheet open={open} onClose={onClose}>
					{content}
				</MobileSheet>
				{profileGate.node}
			</>
		);
	}

	// Desktop: custom centered modal
	return (
		<>
			{createPortal(
				<div className={styles.desktopOverlay} onClick={onClose}>
					<motion.div
						className={styles.desktopModal}
						onClick={(e) => e.stopPropagation()}
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
					>
						<button className={styles.closeBtn} onClick={onClose}>
							&#10005;
						</button>
						{content}
					</motion.div>
				</div>,
				document.body,
			)}
			{profileGate.node}
		</>
	);
};

export default memo(ReactorModal);
